import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useRef, useState } from "react";
import { useGetRecipeImageUrlQuery } from "store/apis/MenusApi";
import { EditSkeleton } from "components/admin/admin.overlays";
import Media from "react-media";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import WarningIcon from "@mui/icons-material/Warning";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ImageModal from "components/shared/imageModal/ImageModal";
import _ from "lodash";
import SubRecipeModal from "components/shared/subRecipeModal/SubRecipeModal";
import MoneyInput from "@nodejsii/react-money-input";
import AllergyOverrideModal from "components/shared/allergyOverrideModal/AllergyOverrideModal";
import SpecialNoteIcon from "@mui/icons-material/AddCircle";
import "components/takeOrder/order/Order.css";

// Shared menu-item selection UI for both the take-order flow (Order.jsx) and order management (AddEditResidentOrder.jsx).
// State that the parent needs for submit/print (orderInfo, selectedSubRecipes, note, charges) is controlled via props;
// state only used for rendering this UI (checked flags, collapsed rows, modals) is kept internal.
export default function OrderMenuDetails({
  residentId,
  menuInfo,
  isMenuLoading,
  isHidden,
  existingOrder, // storedMenuInfo (Order.jsx) or orderData (AddEditResidentOrder.jsx)
  hasExistingOrder, // fromSP / isEditing
  orderInfo,
  setOrderInfo,
  selectedSubRecipes,
  setSelectedSubRecipes,
  noteText,
  setNoteText,
  addlCharges,
  setAddlCharges,
  customChargeText,
  setCustomChargeText,
  showAdditionalCharges,
  setShowAdditionalCharges,
  selectedAddlCharges,
  setSelectedAddlCharges,
  outOfBuilding,
  declinedMeal,
  isAutoSelectItems,
  onFluidRestrictionWarning,
}) {
  const [itemsChecked, setItemsChecked] = useState({
    alternateItems: {},
    menuItems: {},
  });
  const [collapsedRowStatus, setCollapsedRowStatus] = useState([
    { rowId: 0, colStatus: false },
  ]);
  const [isSelectAll, setIsSelectAll] = useState(null);
  const [selectedAltTab, setSelectedAltTab] = useState(0);
  const [imageRecipeId, setImageRecipeId] = useState(0);
  const [recipeImageUrl, setRecipeImageUrl] = useState("");
  const [openRecipeImage, setOpenRecipeImage] = useState(false);
  const [subRecipeModalOpen, setSubRecipeModalOpen] = useState(false);
  const [currentSubRecipe, setCurrentSubRecipe] = useState(null);
  const [currentSubRecipeNote, setCurrentSubRecipeNote] = useState("");
  const [allergyModalOpen, setAllergyModalOpen] = useState(false);
  const [recipeType, setRecipeType] = useState(null);
  const [selectedAllergyRecipe, setSelectedAllergyRecipe] = useState(null);
  const [selectedRemovedItems, setSelectedRemovedItems] = useState([]);
  const [selectedRemovedNote, setSelectedRemovedNote] = useState("");
  const [customChargeErrorText, setCustomChargeErrorText] = useState("");
  const isFirstMenuLoad = useRef(true);

  useEffect(() => {
    if (!customChargeText?.length) {
      setCustomChargeErrorText("Charge Description Required");
    } else if (customChargeText?.length > 32) {
      setCustomChargeErrorText(
        "Charge description must be 32 characters or less"
      );
    } else {
      setCustomChargeErrorText("");
    }
  }, [customChargeText]);

  // restore a previously held/submitted/in-progress order
  useEffect(() => {
    if (hasExistingOrder && existingOrder) {
      let menuItemsChecked = Object.create({});
      let restoredSubRecipes = [];

      existingOrder.menuItems.forEach((item) => {
        menuItemsChecked[item.itemId] = true;

        if (item.subItemList?.length) {
          restoredSubRecipes.push(
            ...item.subItemList.map((sub) => {
              return {
                ...sub,
                parentRecipeId: item.itemId,
                isAlternateItem: false,
              };
            })
          );
          setCollapsedRowStatus((prev) => [
            ...prev,
            { colStatus: true, rowId: item.itemId },
          ]);
        } else if (
          item?.specialNote ||
          item?.removedItems?.length ||
          item?.allergyNote
        ) {
          setCollapsedRowStatus((prev) => [
            ...prev,
            { colStatus: true, rowId: item.itemId },
          ]);
        }
      });

      let altItemsChecked = Object.create({});

      existingOrder.alternateItems?.forEach((item) => {
        altItemsChecked[item.itemId] = true;

        if (item.subItemList?.length) {
          restoredSubRecipes.push(
            ...item.subItemList.map((sub) => {
              return {
                ...sub,
                parentRecipeId: item.itemId,
                isAlternateItem: true,
              };
            })
          );
          setCollapsedRowStatus((prev) => [
            ...prev,
            { colStatus: true, rowId: item.itemId },
          ]);
        }
      });

      setItemsChecked((prev) => ({
        ...prev,
        alternateItems: altItemsChecked,
        menuItems: menuItemsChecked,
      }));

      setSelectedSubRecipes([...restoredSubRecipes]);

      setOrderInfo(() => ({
        facilityMealPrice: existingOrder?.facilityMealPrice ?? 0,
        facilityCharges: existingOrder?.facilityCharges?.length
          ? [...existingOrder.facilityCharges]
          : [],
        guestPrice: existingOrder.guestPrice ?? 0,
        menuItems: [
          ...existingOrder.menuItems.map((item) => {
            return { ...item, subItemList: [...item.subItemList] };
          }),
        ],
        alternateItems: [
          ...existingOrder.alternateItems.map((item) => {
            return { ...item, subItemList: [...item.subItemList] };
          }),
        ],
        order: { ...existingOrder.order },
        mealName: existingOrder.mealName,
      }));

      setNoteText(existingOrder?.order?.note || "");

      if (
        existingOrder?.order?.additionalChargeNote?.length &&
        existingOrder?.order?.additionalCharges > 0
      ) {
        setShowAdditionalCharges(true);
        setCustomChargeText(existingOrder.order.additionalChargeNote);
      }
      setAddlCharges(existingOrder?.order?.additionalCharges || 0.0);

      if (
        existingOrder?.facilityCharges?.length &&
        existingOrder?.order?.orderCharges?.length
      ) {
        const selectedCharges = existingOrder.facilityCharges.filter(
          (charge) =>
            existingOrder.order.orderCharges.some(
              (oc) => oc.facilityChargeTypeId == charge.id
            )
        );
        setSelectedAddlCharges(() => [...selectedCharges]);
      }
    }
  }, [existingOrder]);

  const {
    data: imgUrl,
    isFetching: fetchingUrl,
    isError: urlError,
    isSuccess: urlSuccess,
  } = useGetRecipeImageUrlQuery(imageRecipeId, {
    refetchOnMountOrArgChange: true,
    skip: !imageRecipeId,
  });

  useEffect(() => {
    if (!fetchingUrl && urlSuccess) {
      setRecipeImageUrl(imgUrl);
    }
  }, [fetchingUrl, urlSuccess]);

  useEffect(() => {
    if (urlError) {
      setRecipeImageUrl("");
    }

    if (imageRecipeId) {
      setOpenRecipeImage(true);
    }
  }, [recipeImageUrl, urlError]); //still open with falsy value, template will show error

  useEffect(() => {
    if (menuInfo && !hasExistingOrder) {
      const newOrderInfo = Object.assign({}, menuInfo);
      newOrderInfo["menuItems"] = [];
      newOrderInfo["alternateItems"] = [];
      newOrderInfo["guestPrice"] = 0;
      setOrderInfo(newOrderInfo);
      setItemsChecked({ alternateItems: {}, menuItems: {} });
      setSelectedSubRecipes([]);
      setCollapsedRowStatus([{ rowId: 0, colStatus: false }]);
      setIsSelectAll(null);
    } else if (menuInfo && hasExistingOrder) {
      if (!isFirstMenuLoad.current) {
        // menu changed due to area selection — check if stored selections are still valid
        const newRecipeIds = new Set([
          ...(menuInfo.menuChoices?.map(c => c.recipeId) ?? []),
          ...(menuInfo.alternateChoices?.map(c => c.recipeId) ?? []),
        ]);
        const storedIds = [
          ...(existingOrder?.menuItems?.map(i => i.itemId) ?? []),
          ...(existingOrder?.alternateItems?.map(i => i.itemId) ?? []),
        ];
        const compatible = storedIds.length === 0 || storedIds.every(id => newRecipeIds.has(id));

        if (compatible) {
          // restore stored selections — they may have been cleared by a prior incompatible dining area/menu load
          const checkedMenu = {};
          const checkedAlt = {};
          const subRecipes = [];
          const collapsed = [{ rowId: 0, colStatus: false }];
          existingOrder?.menuItems?.forEach((item) => {
            checkedMenu[item.itemId] = true;
            if (item.subItemList?.length) {
              subRecipes.push(...item.subItemList.map((sub) => ({ ...sub, parentRecipeId: item.itemId, isAlternateItem: false })));
              collapsed.push({ colStatus: true, rowId: item.itemId });
            } else if (item?.specialNote || item?.removedItems?.length || item?.allergyNote) {
              collapsed.push({ colStatus: true, rowId: item.itemId });
            }
          });
          existingOrder?.alternateItems?.forEach((item) => {
            checkedAlt[item.itemId] = true;
            if (item.subItemList?.length) {
              subRecipes.push(...item.subItemList.map((sub) => ({ ...sub, parentRecipeId: item.itemId, isAlternateItem: true })));
              collapsed.push({ colStatus: true, rowId: item.itemId });
            }
          });
          setItemsChecked({ alternateItems: checkedAlt, menuItems: checkedMenu });
          setSelectedSubRecipes(subRecipes);
          setCollapsedRowStatus(collapsed);
          setOrderInfo((prev) => ({
            ...prev,
            menuChoices: [...menuInfo.menuChoices],
            alternateChoices: [...menuInfo.alternateChoices],
            orderId: menuInfo?.orderId,
            roomBed: menuInfo?.roomBed,
            residentName: menuInfo?.residentName,
            menuItems: existingOrder?.menuItems?.map((item) => ({ ...item, subItemList: [...item.subItemList] })) ?? [],
            alternateItems: existingOrder?.alternateItems?.map((item) => ({ ...item, subItemList: [...item.subItemList] })) ?? [],
          }));
        } else {
          setItemsChecked({ alternateItems: {}, menuItems: {} });
          setSelectedSubRecipes([]);
          setCollapsedRowStatus([{ rowId: 0, colStatus: false }]);
          setOrderInfo((prev) => ({
            ...prev,
            menuChoices: [...menuInfo.menuChoices],
            alternateChoices: [...menuInfo.alternateChoices],
            orderId: menuInfo?.orderId,
            roomBed: menuInfo?.roomBed,
            residentName: menuInfo?.residentName,
            menuItems: [],
            alternateItems: [],
          }));
        }
      } else {
        isFirstMenuLoad.current = false;
        // initial load — restore stored selections with fresh menu choices
        const checkedMenu = {};
        const checkedAlt = {};
        const subRecipes = [];
        const collapsed = [{ rowId: 0, colStatus: false }];
        existingOrder?.menuItems?.forEach((item) => {
          checkedMenu[item.itemId] = true;
          if (item.subItemList?.length) {
            subRecipes.push(...item.subItemList.map((sub) => ({ ...sub, parentRecipeId: item.itemId, isAlternateItem: false })));
            collapsed.push({ colStatus: true, rowId: item.itemId });
          } else if (item?.specialNote || item?.removedItems?.length || item?.allergyNote) {
            collapsed.push({ colStatus: true, rowId: item.itemId });
          }
        });
        existingOrder?.alternateItems?.forEach((item) => {
          checkedAlt[item.itemId] = true;
          if (item.subItemList?.length) {
            subRecipes.push(...item.subItemList.map((sub) => ({ ...sub, parentRecipeId: item.itemId, isAlternateItem: true })));
            collapsed.push({ colStatus: true, rowId: item.itemId });
          }
        });
        setItemsChecked({ alternateItems: checkedAlt, menuItems: checkedMenu });
        setSelectedSubRecipes(subRecipes);
        setCollapsedRowStatus(collapsed);
        setOrderInfo((prev) => ({
          ...prev,
          menuChoices: [...menuInfo.menuChoices],
          alternateChoices: [...menuInfo.alternateChoices],
          orderId: menuInfo?.orderId,
          roomBed: menuInfo?.roomBed,
          residentName: menuInfo?.residentName,
          menuItems: existingOrder?.menuItems?.map((item) => ({ ...item, subItemList: [...item.subItemList] })) ?? [],
          alternateItems: existingOrder?.alternateItems?.map((item) => ({ ...item, subItemList: [...item.subItemList] })) ?? [],
        }));
      }
    }
  }, [menuInfo]);

  useEffect(() => {
    // isSelectAll may already be true from a prior menu loading and auto select being applied, 
    // so re-arm through null to force the select-all effect to rerun on each new menu that loads
    if (isAutoSelectItems && !hasExistingOrder && menuInfo && isSelectAll === null) {
      setIsSelectAll(true);
    }
  }, [isSelectAll, menuInfo, isAutoSelectItems, hasExistingOrder]);

  useEffect(() => {
    if (isSelectAll !== null) {
      //guest price not being reflected accurately (only adding last guestPrice, concurrency issue)
      const guestPriceBefore = orderInfo?.["guestPrice"] ?? 0;
      let guestPriceTotal = guestPriceBefore;

      menuInfo?.menuChoices?.forEach((item) => {
        if (!item?.isAllergy) {
          const found = orderInfo["menuItems"].some(
            (el) => el.itemId === item.recipeId
          );
          if (!found && isSelectAll) {
            guestPriceTotal += item?.guestPrice || 0;
          } else if (found && !isSelectAll) {
            guestPriceTotal -= item?.guestPrice || 0;
          }
          handleMenuItemClick(item, "menuItems", isSelectAll, true);
        }
      });

      // concurrency memory reference issue needs workaround for deselect all
      // it removes from the checked item lists properly, so visually it looks fine
      // but ends up leaving the actual items in the array. This bug exists in legacy
      // and a reset of menuItems to an empty array in the state object is the simplest solution
      if (!isSelectAll) {
        setOrderInfo((prev) => ({
          ...prev,
          menuItems: [],
          guestPrice: guestPriceTotal,
        }));
      } else {
        setOrderInfo((prev) => ({
          ...prev,
          guestPrice: guestPriceTotal,
        }));
      }
    }
  }, [isSelectAll]);

  useEffect(() => {
    if (outOfBuilding || declinedMeal) {
      // menu is hidden entirely in this state, so clear all selections to avoid submitting a stale order
      setIsSelectAll(false);
      setOrderInfo((prev) => ({
        ...prev,
        menuItems: [],
        alternateItems: [],
      }));
      setSelectedSubRecipes([]);
      setItemsChecked({
        alternateItems: {},
        menuItems: {},
      });
    }
  }, [outOfBuilding, declinedMeal]);

  const removeSelectedSubRecipeFromList = (parentRecipeId, isAltRecipe) => {
    setSelectedSubRecipes((prev) => [
      ...prev.filter(
        (sub) =>
          sub.parentRecipeId !== parentRecipeId ||
          sub.isAlternateItem !== isAltRecipe
      ),
    ]);
    setCollapsedRowStatus((prev) => [
      ...prev.filter(
        (row) =>
          row.rowid !== parentRecipeId || row.isAlternateItem !== isAltRecipe
      ),
    ]);
  };

  const handleMenuItemClick = (item, choice, isChecked, isFromSelectAll = false) => {
    const tempOrderInfo = Object.assign({}, orderInfo);
    const tempItemsChecked = itemsChecked;
    let guestPrice = orderInfo?.["guestPrice"] ?? 0;

    if (isChecked) {
      let found = tempOrderInfo[choice].some(
        (el) => el.itemId === item.recipeId
      );
      if (!found) {
        tempOrderInfo[choice].push({
          itemId: item.recipeId,
          quantity: 1,
          guestPrice: item.guestPrice,
          portionSize: item.portionSize,
          name: item.recipeName,
          menuCategory: item.menuCategory,
          isSubstitution: item.isSubstitution,
        });

        tempItemsChecked[choice][item.recipeId] = true;
        guestPrice += item.guestPrice;

        if (menuInfo.fluidRestriction && item.applicableToFluidRestriction) {
          onFluidRestrictionWarning?.();
        }

        if (item && item?.subRecipeList?.length && !isFromSelectAll) {
          renderSubMenuModal(null, item);
        }
      }
    } else {
      tempOrderInfo[choice] = tempOrderInfo[choice]?.filter((orderItem) => {
        if (orderItem.itemId === item.recipeId) {
          guestPrice = guestPrice - orderItem.guestPrice * orderItem.quantity;
          orderItem.quantity = "";
        }
        return orderItem.itemId !== item.recipeId;
      });

      tempItemsChecked[choice][item.recipeId] = false;
      removeSelectedSubRecipeFromList(item.recipeId, item.isAlternateRecipe);
    }

    setItemsChecked(tempItemsChecked);

    setOrderInfo((prev) => ({
      ...prev,
      alternateItems: [...tempOrderInfo.alternateItems],
      menuItems: [...tempOrderInfo.menuItems],
      guestPrice: +guestPrice?.toFixed(2),
    }));
  };

  const selectQuantity = (item, choice, value) => {
    const tempOrderInfo = Object.assign({}, orderInfo);

    tempOrderInfo[choice]?.forEach((orderItem) => {
      if (orderItem.itemId === item.recipeId) {
        setOrderInfo((prev) => ({
          ...prev,
          guestPrice:
            prev.guestPrice + (value - orderItem.quantity) * item.guestPrice,
        }));

        orderItem.quantity = value;
      }
    });
  };

  const renderQuantity = (maxQuantity) => {
    const sizes = [];
    const servingSizes = [1 / 2];

    for (let i = 1; i <= maxQuantity; i++) {
      servingSizes.push(i);
    }

    servingSizes.forEach((size) => {
      sizes.push(
        <MenuItem key={size} value={size}>
          <span className="general-text">{size}</span>
        </MenuItem>
      );
    });

    return sizes;
  };

  const renderSubMenuModal = (event, subRecipe) => {
    if (event) {
      event.stopPropagation();
    }

    if (subRecipe.isAlternateRecipe) {
      const altItem = orderInfo.alternateItems.find(
        (item) => item.itemId === subRecipe.recipeId
      );
      setCurrentSubRecipeNote(altItem?.specialNote || "");
    } else {
      const menuItem = orderInfo.menuItems.find(
        (item) => item.itemId === subRecipe.recipeId
      );
      setCurrentSubRecipeNote(menuItem?.specialNote || "");
    }
    setCurrentSubRecipe(subRecipe);
    setSubRecipeModalOpen(true);
  };

  const updateSubRecipeAndAddlFields = (
    recipeId,
    isAlternate,
    selectedSubRecipesArg = [],
    specialNote = "",
    isAllergyOverride = false,
    allergyNote = "",
    removedItems = []
  ) => {
    if (!isAlternate) {
      const rowId = recipeId;
      let tempCollapseStatus = collapsedRowStatus;
      const currentItem = orderInfo?.[
        isAlternate ? "alternateItems" : "menuItems"
      ]?.find((item) => item.itemId === recipeId);
      const totalSubsForItem =
        selectedSubRecipesArg?.length ||
        currentItem?.allergyNote?.length ||
        allergyNote?.length ||
        currentItem?.removedItems?.length ||
        removedItems?.length ||
        currentItem?.specialNote?.length ||
        specialNote?.length ||
        0;

      if (totalSubsForItem > 0) {
        const temp = tempCollapseStatus.filter((row) => row.rowId === rowId);

        if (temp?.length > 0) {
          temp.at(0).colStatus = true;
        } else {
          tempCollapseStatus.push({ rowId: rowId, colStatus: true });
        }
      } else {
        tempCollapseStatus = tempCollapseStatus.filter(
          (row) => row.rowId !== rowId
        );
      }

      setCollapsedRowStatus(tempCollapseStatus);
    }

    const menuItem = orderInfo.menuItems.find(
      (item) => item.itemId === recipeId
    );
    const altItem = orderInfo.alternateItems.find(
      (item) => item.itemId === recipeId
    );

    //some menus have identical alternates included in menu at the same time
    //seems unlikely they would both be selected for an order but just in case
    //check which one they actually selected by the alternate flag
    if (menuItem && !isAlternate) {
      menuItem["specialNote"] = specialNote;
      if (isAllergyOverride) {
        menuItem["allergyNote"] = allergyNote;
        menuItem["removedItems"] = [...removedItems];
      }
    } else if (altItem && isAlternate) {
      altItem["specialNote"] = specialNote;
      if (isAllergyOverride) {
        altItem["allergyNote"] = allergyNote;
        altItem["removedItems"] = [...removedItems];
      }
    }

    if (!isAllergyOverride) {
      setSelectedSubRecipes((prev) => [
        ...prev.filter((old) => old.parentRecipeId !== recipeId),
        ...selectedSubRecipesArg,
      ]);
    }
  };

  const handleSubRecipeClose = (
    recipeId,
    selectedSubRecipesArg,
    specialNote = ""
  ) => {
    updateSubRecipeAndAddlFields(
      recipeId,
      currentSubRecipe?.isAlternateRecipe,
      selectedSubRecipesArg,
      specialNote
    );
    setSubRecipeModalOpen(false);
  };

  const handlePopUpImage = (recipeId) => {
    // if we already have the imageUrl for that recipe, just open the modal
    if (imageRecipeId === recipeId) {
      setOpenRecipeImage(true);
    } else {
      setImageRecipeId(recipeId);
    }
  };

  const renderMenuItems = () => {
    const menuItems = [];

    if (menuInfo) {
      menuInfo?.menuChoices?.forEach((item) => {
        let selectedValue = "";
        let specialNote = "";
        let allergyNote = "";
        let removedItems = [];
        let showArrowIcon = false;

        orderInfo?.menuItems?.forEach((orderItem) => {
          if (orderItem?.itemId === item.recipeId) {
            selectedValue = orderItem?.quantity;
            if (orderItem?.allergyNote) {
              allergyNote = orderItem.allergyNote;
            }

            if (orderItem?.specialNote) {
              specialNote = orderItem.specialNote;
            }

            if (orderItem?.removedItems?.length) {
              removedItems = [...orderItem.removedItems];
            }

            showArrowIcon =
              selectedSubRecipes?.filter(
                (sub) =>
                  sub.parentRecipeId === orderItem.itemId &&
                  !sub.isAlternateItem
              )?.length ||
              allergyNote?.length ||
              specialNote?.length ||
              removedItems?.length;
          }
        });

        if (selectedValue === undefined) selectedValue = "";

        let flag = itemsChecked?.menuItems?.[item.recipeId];

        let rowStatus = collapsedRowStatus;
        let status = rowStatus?.filter((row) => row?.rowId === item.recipeId);
        let tempStatus = status.length ? status[0]?.colStatus : false;

        menuItems.push(
          <TableRow
            key={item.recipeId}
            sx={{
              borderBottom: "1px solid rgba(128, 128, 128, .5)",
              backgroundColor: flag ? "#cee2e3" : "#fff",
              width: "100%",
              borderRadius: "1rem",
              display: "flex",
              justifyContent: "flex-start",
              margin: ".25rem 0",
            }}>
            <TableCell
              className="noBorderCell"
              style={{ width: "10px", justifyContent: "center", padding: "0" }}>
              <Grid container justifyContent="center" alignItems="center">
                {!!showArrowIcon && (
                  <IconButton
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (status?.length) {
                        status[0].colStatus = !tempStatus;
                        setCollapsedRowStatus([...rowStatus]);
                      }
                    }}>
                    {tempStatus ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </IconButton>
                )}
              </Grid>
            </TableCell>
            <TableCell
              className="noBorderCell"
              style={{
                padding: "0",
                cursor: "pointer",
                width: "-webkit-fill-available",
              }}
              onClick={() => {
                //non-allergy or already marked allergy
                if (!item.isAllergy || (item.isAllergy && flag)) {
                  handleMenuItemClick(item, "menuItems", !flag);
                } else {
                  //unmarked allergy
                  setAllergyModalOpen(true);
                  setRecipeType("menuItems");
                  setSelectedRemovedItems(removedItems);
                  setSelectedRemovedNote(allergyNote);
                  setSelectedAllergyRecipe(item);
                }
              }}>
              <Grid
                container
                justifyContent="flex-start"
                alignItems="flex-start">
                <Media query="(max-width: 599px)">
                  {(matches) =>
                    matches ? (
                      <span
                        style={{
                          fontSize: "1.75rem",
                          fontWeight: "fontWeightMedium",
                        }}>
                        {item.recipeName}
                      </span>
                    ) : (
                      <span
                        style={{
                          fontSize: "1.75rem",
                          fontWeight: "fontWeightMedium",
                        }}>
                        {item.recipeName}
                      </span>
                    )
                  }
                </Media>
              </Grid>
            </TableCell>
            <TableCell className="noBorderCell" sx={{ marginLeft: "auto" }}>
              {item.isAllergy && (
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  flexWrap="nowrap">
                  <WarningIcon sx={{ cursor: "pointer" }} className="warning" />
                  <Typography className="warning">
                    <span className="warning">{" ALLERGY"}</span>
                  </Typography>
                </Grid>
              )}
            </TableCell>
            <TableCell className="noBorderCell" sx={{ padding: "0" }}>
              <Grid
                container
                justifyContent="flex-end"
                alignItems="center"
                flexWrap="nowrap">
                {flag && (
                  <SpecialNoteIcon
                    style={{
                      color: "#505050",
                      marginRight: "5px",
                      backgroundColor: "transparent",
                      cursor: "pointer",
                      height: "1.75rem",
                      width: "1.75rem",
                      fontSize: "1.75rem !important",
                    }}
                    onClick={(event) => renderSubMenuModal(event, item)}
                  />
                )}

                {item.imageAvailable && (
                  <CameraAltIcon
                    style={{ color: "#2F4F4F", marginRight: "5px" }}
                    onClick={() => handlePopUpImage(item.recipeId)}
                  />
                )}
                {item.imageAvailable && (
                  <ImageModal
                    open={openRecipeImage}
                    sourceUrl={recipeImageUrl}
                    handleClose={() => setOpenRecipeImage(false)}
                  />
                )}

                <Select
                  variant="standard"
                  style={{ marginRIght: "5px" }}
                  disabled={!flag}
                  value={selectedValue}
                  onChange={(event) =>
                    selectQuantity(item, "menuItems", event.target.value)
                  }
                  onClick={(e) => e.stopPropagation()}>
                  {renderQuantity(item.maxNumberOfServings)}
                </Select>
              </Grid>
            </TableCell>
          </TableRow>
        );
        const subRecipes = selectedSubRecipes?.filter(
          (sub) => sub.parentRecipeId === item.recipeId && !sub.isAlternateItem
        );

        if (specialNote) {
          subRecipes.splice(0, 0, {
            recipeId: `0-${specialNote}`,
            note: `Special Note: ${specialNote}`,
            isAllergyNote: true,
          }); //so it prints properly
        }

        if (allergyNote) {
          subRecipes.splice(0, 0, {
            recipeId: `-1-${allergyNote}`,
            note: allergyNote,
            isAllergyNote: true,
          });
        }

        if (removedItems?.length) {
          const subMapping = removedItems.map((removed, idx) => {
            return {
              recipeId: `{${-2 - idx}-${removed.text}`,
              note: `Remove - ${removed.text} (ALLERGY)`,
              isAllergyNote: true,
            };
          });

          subRecipes.splice(0, 0, ...subMapping);
        }

        subRecipes.forEach((subRecipe) => {
          menuItems.push(
            // Weird nesting but needed to work around DOM nesting errors (td child of div) with Collapse element being inserted into table structure
            <TableRow
              key={subRecipe.recipeId}
              sx={{
                height: tempStatus ? "auto" : "0px !important",
                borderBottom: tempStatus
                  ? "1px solid rgba(128, 128, 128, .5)"
                  : "none",
              }}>
              <TableCell
                className="noBorderCell"
                sx={{ padding: "0 !important" }}>
                <div
                  style={{
                    backgroundColor: "#cee2e3",
                    width: "100%",
                    borderRadius: "1rem",
                    justifyContent: "flex-start",
                    margin: tempStatus ? ".25rem 0" : "0",
                    padding: tempStatus ? ".25rem 0" : "0",
                  }}>
                  <Collapse in={tempStatus} timeout="auto" unmountOnExit>
                    <Table sx={{ marginLeft: "3rem" }}>
                      <TableBody sx={{ padding: ".125rem" }}>
                        <TableRow>
                          <TableCell className="noBorderCell">
                            <Typography
                              sx={{
                                fontSize: "1.75rem",
                                fontStyle: "italic",
                                lineHeight: "1",
                                paddingY: ".125rem",
                              }}>
                              {subRecipe?.isAllergyNote
                                ? `${subRecipe.note}`
                                : `${subRecipe.recipeName} - ${subRecipe.quantity}`}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Collapse>
                </div>
              </TableCell>
            </TableRow>
          );
        });
      });
    }

    return menuItems;
  };

  const handleTabChange = (event, newValue) => {
    setSelectedAltTab(newValue);
  };

  const renderAlternateItemsTabs = () => {
    const alternateTabs = [];
    let tabstrip = <></>;

    if (menuInfo !== null) {
      const categoryItems =
        menuInfo?.alternateChoices?.filter(
          (item) => item?.menuCategory?.trim()?.toUpperCase() !== "NO CATEGORY"
        ) ?? [];
      const noCategoryItems =
        menuInfo?.alternateChoices?.filter(
          (item) => item?.menuCategory?.trim()?.toUpperCase() === "NO CATEGORY"
        ) ?? [];

      const reorderedChoices = [...categoryItems, ...noCategoryItems];

      const groupedItems = _.groupBy(
        reorderedChoices,
        _.property("menuCategory")
      );

      let idx = 0;
      for (const key in groupedItems) {
        if (groupedItems[key]?.length) {
          alternateTabs.push(<Tab key={key} label={key} value={idx} />);
          idx++;
        }
      }

      tabstrip = (
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{ marginBottom: "1rem", overflowX: "hidden", width: "100%" }}>
            <Tabs
              id="altTabs"
              value={selectedAltTab}
              onChange={handleTabChange}
              variant={"scrollable"}
              scrollButtons={"auto"}
              sx={{
                margin: "auto",
                maxWidth: "100%",
                justifyContent: "center",
              }}
              allowScrollButtonsMobile
              TabIndicatorProps={{
                sx: {
                  display: "none",
                  width: "100%",
                },
              }}>
              {alternateTabs}
            </Tabs>
          </Box>
          {Object.keys(groupedItems).map((key, idx) => {
            return (
              <div
                key={`${key}-${idx}`}
                hidden={selectedAltTab !== idx}
                role="tabpanel">
                {selectedAltTab === idx && (
                  <Table id="alt-table">
                    <TableBody>{renderListItems(groupedItems[key])}</TableBody>
                  </Table>
                )}
              </div>
            );
          })}
        </Box>
      );
    }

    return tabstrip;
  };

  const renderListItems = (items) => {
    const list = [];

    _.uniqBy(items, _.property("recipeId"))?.forEach((item, idx) => {
      let flag = itemsChecked?.alternateItems?.[item.recipeId];
      let selectedValue = "";
      let specialNote = "";
      let allergyNote = "";
      let removedItems = [];

      orderInfo?.alternateItems?.forEach((alt) => {
        if (alt.itemId === item.recipeId) {
          selectedValue = alt.quantity ?? undefined;

          if (alt?.allergyNote) {
            allergyNote = alt.allergyNote;
          }

          if (alt?.specialNote) {
            specialNote = alt.specialNote;
          }

          if (alt?.removedItems?.length) {
            removedItems = [...alt.removedItems];
          }
        }
      });

      if (selectedValue === undefined) {
        selectedValue = "";
      }

      list.push(
        <TableRow
          key={`${item.recipeId}-${idx}`}
          sx={{
            borderBottom: "1px solid rgba(128, 128, 128, .5)",
            backgroundColor: flag ? "#cee2e3" : "#FFF",
            width: "100%",
            borderRadius: "1rem",
            display: "flex",
            justifyContent: "flex-start",
            padding: ".25rem 0",
            margin: ".25rem 0",
          }}>
          <TableCell
            className="noBorderCell"
            sx={{ cursor: "pointer", width: "-webkit-fill-available" }}
            onClick={() => {
              //non-allergy or already marked allergy
              if (!item.isAllergy || (item.isAllergy && flag)) {
                handleMenuItemClick(item, "alternateItems", !flag);
              } else {
                //unmarked allergy
                setAllergyModalOpen(true);
                setRecipeType("alternateItems");
                setSelectedRemovedItems(removedItems);
                setSelectedRemovedNote(allergyNote);
                setSelectedAllergyRecipe(item);
              }
            }}>
            <Grid container justifyContent="flex-start" alignItems="flex-start">
              <Media query="(max-width: 599px)">
                {(matches) =>
                  matches ? (
                    <span
                      style={{
                        fontSize: "1.75rem",
                        fontWeight: "fontWeightMedium",
                      }}>
                      {item.recipeName}
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: "1.75rem",
                        fontWeight: "fontWeightMedium",
                      }}>
                      {item.recipeName}
                    </span>
                  )
                }
              </Media>
            </Grid>
          </TableCell>
          <TableCell sx={{ marginLeft: "auto" }} className="noBorderCell">
            {item.isAllergy && (
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                flexWrap="nowrap">
                <WarningIcon
                  sx={{ cursor: "pointer" }}
                  className="warning"
                  onClick={() => {
                    setAllergyModalOpen(true);
                    setRecipeType("alternateItems");
                    setSelectedRemovedItems(removedItems);
                    setSelectedRemovedNote(allergyNote);
                    setSelectedAllergyRecipe(item);
                  }}
                />
                <Typography className="warning">
                  <span className="warning">{" ALLERGY"}</span>
                </Typography>
              </Grid>
            )}
          </TableCell>
          <TableCell className="noBorderCell" sx={{ padding: "0" }}>
            <Grid
              container
              justifyContent="flex-end"
              alignItems="center"
              flexWrap="nowrap">
              {flag && (
                <SpecialNoteIcon
                  style={{
                    color: "#505050",
                    marginRight: "5px",
                    backgroundColor: "transparent",
                    cursor: "pointer",
                    height: "1.75rem",
                    width: "1.75rem",
                    fontSize: "1.75rem !important",
                  }}
                  onClick={(event) => renderSubMenuModal(event, item)}
                />
              )}

              {item.imageAvailable && (
                <CameraAltIcon
                  style={{ color: "#2F4F4F", marginRight: "5px" }}
                  onClick={() => handlePopUpImage(item.recipeId)}
                />
              )}
              {item.imageAvailable && (
                <ImageModal
                  open={openRecipeImage}
                  sourceUrl={recipeImageUrl}
                  handleClose={() => setOpenRecipeImage(false)}
                />
              )}

              <Select
                variant="standard"
                style={{ marginRIght: "5px" }}
                disabled={!flag}
                value={selectedValue}
                onChange={(event) =>
                  selectQuantity(item, "alternateItems", event.target.value)
                }
                onClick={(e) => e.stopPropagation()}>
                {renderQuantity(item.maxNumberOfServings)}
              </Select>
            </Grid>
          </TableCell>
        </TableRow>
      );

      const subRecipes = selectedSubRecipes.filter(
        (sub) => sub.parentRecipeId === item.recipeId && sub.isAlternateItem
      );

      if (specialNote) {
        subRecipes.splice(0, 0, {
          recipeId: `0-${specialNote}`,
          note: `Special Note: ${specialNote}`,
          isAllergyNote: true,
        }); //so it prints properly
      }

      if (allergyNote) {
        subRecipes.splice(0, 0, {
          recipeId: `-1-${allergyNote}`,
          note: allergyNote,
          isAllergyNote: true,
        });
      }

      if (removedItems?.length) {
        const subMapping = removedItems.map((removed, idx) => {
          return {
            recipeId: `{${-2 - idx}-${removed.text}`,
            note: `Remove - ${removed.text} (ALLERGY)`,
            isAllergyNote: true,
          };
        });

        subRecipes.splice(0, 0, ...subMapping);
      }

      _.uniqBy(subRecipes, _.property("recipeId")).forEach((subRecipe, idx) => {
        list.push(
          <TableRow
            key={`${subRecipe.recipeId}-${idx}`}
            sx={{
              borderBottom: "1px solid rgba(128, 128, 128, .5)",
              backgroundColor: "#cee2e3",
              width: "100%",
              borderRadius: "1rem",
              height: "2.5rem",
              display: "flex",
              alignContent: "center",
              margin: ".25rem 0",
            }}>
            <TableCell className="noBorderCell">
              <span
                style={{
                  lineHeight: "1",
                  paddingLeft: "4rem",
                  fontSize: "1.75rem",
                  fontStyle: "italic",
                }}>
                {subRecipe?.isAllergyNote
                  ? `${subRecipe.note}`
                  : `${subRecipe.recipeName} - ${subRecipe.quantity}`}
              </span>
            </TableCell>
          </TableRow>
        );
      });
    });

    return list;
  };

  const handleAllergyClose = ({ removedItems, note }) => {
    setAllergyModalOpen(false);
    if (note || removedItems?.length) {
      //they don't have to remove things but they do have to add a note
      handleMenuItemClick(selectedAllergyRecipe, recipeType, true);
      const allergyNote = note?.trim()?.length ? `Allergy Note: ${note}` : "";
      updateSubRecipeAndAddlFields(
        selectedAllergyRecipe.recipeId,
        selectedAllergyRecipe?.isAlternateRecipe,
        [],
        "",
        true,
        allergyNote,
        removedItems
      );
    }
  };

  const handleAddlChargeClick = (charge) => {
    const idx = selectedAddlCharges.findIndex(
      (oldCharge) => oldCharge.id === charge.id
    );
    if (idx >= 0) {
      setSelectedAddlCharges((prev) => {
        const copyCharges = [...prev];
        copyCharges.splice(idx, 1);
        return copyCharges;
      });
    } else {
      setSelectedAddlCharges((prev) => [...prev, charge]);
    }
  };

  const renderFacilityCustomCharges = () => {
    const uiList = [];

    orderInfo?.facilityCharges?.forEach((charge) => {
      const flag = selectedAddlCharges.some(
        (selected) => selected.id === charge.id
      );

      uiList.push(
        <TableRow
          sx={{
            borderBottom: "1px solid rgba(128, 128, 128, .5)",
            backgroundColor: flag ? "#cee2e3" : "#FFF",
            width: "100%",
            borderRadius: "1rem",
            display: "flex",
            justifyContent: "flex-start",
            padding: ".25rem 0",
            margin: ".25rem 0",
            marginBottom: ".25rem",
          }}
          key={`${charge.id}`}
          onClick={() => handleAddlChargeClick(charge)}>
          <TableCell
            className="noBorderCell"
            sx={{
              alignSelf: "center",
              paddingLeft: "4rem !important",
              width: "-webkit-fill-available",
            }}>
            <Typography variant="body1">
              {charge?.chargeTypeId !== 6
                ? charge.chargeTypeName
                : charge.chargeTypeText}
            </Typography>
          </TableCell>
          <TableCell
            className="noBorderCell"
            sx={{
              alignSelf: "center",
              paddingRight: "4rem !important",
            }}>
            <Typography variant="body1">{`$${charge.price.toFixed(
              2
            )}`}</Typography>
          </TableCell>
        </TableRow>
      );
    });

    uiList.push(
      <TableRow key="customCharge">
        <TableCell className="noBorderCell">
          <Grid
            container
            alignItems="flex-start"
            justifyContent="space-between"
            marginTop=".5rem"
            flexWrap="nowrap">
            <Button
              variant="contained"
              sx={{
                marginLeft: "2%",
                marginTop: "2px",
                fontWeight: "bold",
                color: "#000",
                backgroundColor: "#FDB92E",
              }}
              onClick={() => {
                setShowAdditionalCharges((prev) => !prev);
                setAddlCharges(0.0);
                setCustomChargeText("");
              }}>
              Custom Charge
            </Button>

            {showAdditionalCharges && (
              <TextField
                id="textarea"
                className="chargeTextarea"
                color="transparent"
                variant="outlined"
                sx={{ width: "40%", marginBottom: "0px !important" }}
                placeholder="Charge description"
                size="small"
                value={customChargeText}
                required={showAdditionalCharges}
                error={
                  (showAdditionalCharges && !customChargeText?.length) ||
                  (showAdditionalCharges && customChargeText?.length > 32)
                }
                helperText={customChargeErrorText}
                // onChange fires too much to replace in object reference each time
                // (overhead concern)
                // store in state and replace before submit/hold
                onChange={(event) => {
                  setCustomChargeText(event.target.value);
                }}
              />
            )}

            {showAdditionalCharges && (
              <div
                style={{
                  marginLeft: "auto !important",
                  display: "inline",
                  marginTop: ".5rem",
                }}>
                <MoneyInput
                  className="addlCharge"
                  value={addlCharges}
                  // onChange fires too much to replace in object reference each time
                  // (overhead concern)
                  // store in state and replace before submit/hold
                  onChange={(event) => setAddlCharges(event.target.value)}
                />
                {addlCharges > 100 && (
                  <div style={{ display: "inline-flex", marginLeft: "auto" }}>
                    <p style={{ color: "red", fontSize: ".75rem" }}>
                      Charge must be $100 or less
                    </p>
                  </div>
                )}
              </div>
            )}
          </Grid>
        </TableCell>
      </TableRow>
    );

    return uiList;
  };

  // rendered internally (rather than being unmounted by the parent) so hydration state above survives
  // transient parent-level flips (setup warnings, loading pulses) instead of resetting on every remount
  if (isHidden) {
    return null;
  }
  if (isMenuLoading) {
    return <EditSkeleton height="250px" />;
  }

  return (
    <>
      {!outOfBuilding && !declinedMeal && (
        <>
          <Card className="overCard">
            <CardContent
              sx={{
                padding: ".25rem .75rem .25rem .75rem !important",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#d9d9d9",
                borderRadius: ".25rem",
              }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  textTransform: "uppercase",
                }}>
                Daily Special
              </Typography>
              <Button
                variant="contained"
                sx={{
                  fontWeight: "bold",
                  margin: ".25rem .75rem 0",
                  color: "#FFF",
                  backgroundColor: "var(--tealColor)",
                }}
                onClick={() => setIsSelectAll((prev) => !prev)}>
                {isSelectAll ? "UNSELECT ALL" : "SELECT ALL"}
              </Button>
            </CardContent>
          </Card>
          <Card className="underCard">
            <CardContent sx={{ marginTop: "1rem" }}>
              <Table id="menu-table">
                <TableBody>{renderMenuItems()}</TableBody>
              </Table>
            </CardContent>
          </Card>
          <Card className="overCard">
            <CardContent
              sx={{
                padding: ".25rem .75rem .25rem .75rem !important",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#d9d9d9",
                borderRadius: ".25rem",
              }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  textTransform: "uppercase",
                }}>
                Other Menu Options
              </Typography>
            </CardContent>
          </Card>
          <Card className="underCard">
            <CardContent sx={{ marginTop: "1rem" }}>
              {renderAlternateItemsTabs()}
            </CardContent>
          </Card>
          <Card className="overCard">
            <CardContent
              sx={{
                padding: ".25rem .75rem .25rem .75rem !important",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#d9d9d9",
                borderRadius: ".25rem",
              }}>
              <Typography
                variant="h5"
                sx={{ textTransform: "uppercase", fontWeight: "bold" }}>
                Note
              </Typography>
            </CardContent>
          </Card>
          <Card className="underCard">
            <CardContent sx={{ padding: "2.5rem .25rem" }}>
              <TextField
                id="textarea"
                className="noteTextarea"
                multiline
                rows={3}
                color="transparent"
                fullWidth
                sx={{ width: "96%", marginX: "2%" }}
                value={noteText}
                // onChange fires too much to replace in object reference each time
                // (overhead concern)
                // store in state and replace before submit/hold
                onChange={(event) => {
                  setNoteText(event.target.value);
                }}
              />
            </CardContent>
          </Card>
          <Card className="overCard">
            <CardContent
              sx={{
                padding: ".25rem .75rem .25rem .75rem !important",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#d9d9d9",
                borderRadius: ".25rem",
              }}>
              <Typography
                variant="h5"
                sx={{ textTransform: "uppercase", fontWeight: "bold" }}>
                Additional Charges
              </Typography>
            </CardContent>
          </Card>
          <Card className="underCard">
            <CardContent sx={{ padding: "2.5rem .25rem" }}>
              <Table id="price-table">
                <TableBody>{renderFacilityCustomCharges()}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
      {subRecipeModalOpen && (
        <SubRecipeModal
          open={subRecipeModalOpen}
          selectedRecipe={currentSubRecipe}
          currentSubRecipes={selectedSubRecipes}
          specialNote={currentSubRecipeNote}
          handleClose={handleSubRecipeClose}
        />
      )}
      {allergyModalOpen && (
        <AllergyOverrideModal
          open={allergyModalOpen}
          residentId={residentId}
          currentRemovedItems={selectedRemovedItems}
          currentNote={selectedRemovedNote}
          recipeId={selectedAllergyRecipe?.recipeId}
          handleClose={handleAllergyClose}
        />
      )}
    </>
  );
}